import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import content from "../content";

// Contador animado (cuenta hacia arriba cuando entra en pantalla).
function StatCounter({ value, suffix, label }) {
  const ref = useRef(null);
  const [n, setN] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    let started = false;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          started = true;
          const start = performance.now();
          const dur = 1500;
          const tick = (now) => {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(eased * value));
            if (p < 1) raf = requestAnimationFrame(tick);
          };
          raf = requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value]);

  return (
    <div ref={ref} className="text-center lg:text-left" data-testid={`recap-stat-${label}`}>
      <p className="font-anton text-white text-4xl sm:text-5xl md:text-6xl leading-none">
        {n}
        <span className="text-electric">{suffix}</span>
      </p>
      <p className="font-poppins text-ceramic/70 text-[11px] md:text-sm uppercase tracking-[0.16em] mt-2">
        {label}
      </p>
    </div>
  );
}

export default function FestivalRecapSection() {
  const r = content.festivalRecap;
  const reduced = useReducedMotion();
  const images = r?.images ?? [];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (reduced || images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4000);
    return () => clearInterval(t);
  }, [reduced, images.length]);

  if (!r || r.hidden) return null;

  const go = (d) => setIdx((i) => (i + d + images.length) % images.length);

  return (
    <section
      id="gracias"
      className="relative w-full bg-ink py-24 md:py-32 overflow-hidden"
      data-testid="festival-recap-section"
    >
      {/* Fondo con slideshow difuminado */}
      <div className="absolute inset-0" aria-hidden="true">
        <AnimatePresence initial={false}>
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 0.28, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.3, ease: "easeInOut" }}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${images[idx]?.src})` }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-ink/70" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(circle at 18% 22%, rgba(33,112,232,0.18), transparent 55%)" }}
        />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Texto + estadísticas */}
          <Reveal className="order-2 lg:order-1">
            <Rise>
              <span className="inline-flex items-center gap-2 rounded-full border border-electric/30 bg-electric/10 px-4 py-1.5 mb-6">
                <Sparkles size={15} className="text-electric" />
                <span className="font-poppins text-[11px] tracking-[0.28em] uppercase text-electric font-semibold">
                  {r.eyebrow}
                </span>
              </span>
            </Rise>
            <Rise>
              <h2 className="display text-white text-[clamp(3rem,9vw,6rem)] leading-[0.9] mb-5">
                {r.title}
              </h2>
            </Rise>
            <Rise>
              <p className="font-poppins text-ceramic/80 text-base md:text-lg leading-relaxed max-w-xl mb-10">
                {r.text}
              </p>
            </Rise>
            <Rise>
              <div className="grid grid-cols-3 gap-4 md:gap-6 max-w-xl">
                {r.stats.map((s) => (
                  <StatCounter key={s.label} value={s.value} suffix={s.suffix} label={s.label} />
                ))}
              </div>
            </Rise>
          </Reveal>

          {/* Slideshow interactivo */}
          <Reveal className="order-1 lg:order-2">
            <Rise>
              <div
                className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] aspect-[4/3] bg-white/5"
                data-testid="recap-slideshow"
              >
                <AnimatePresence initial={false}>
                  <motion.img
                    key={idx}
                    src={images[idx]?.src}
                    alt={images[idx]?.alt}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />

                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Foto anterior"
                  data-testid="recap-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center hover:bg-electric transition-colors duration-300"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Foto siguiente"
                  data-testid="recap-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center hover:bg-electric transition-colors duration-300"
                >
                  <ChevronRight size={20} />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIdx(i)}
                      aria-label={`Ver foto ${i + 1}`}
                      data-testid={`recap-dot-${i}`}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        i === idx ? "w-7 bg-electric" : "w-2 bg-white/50 hover:bg-white/80"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </Rise>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
